import { useCallback } from 'react';

import { FirstName, LastName } from '@dreryk/person-name-common-api';
import { PhoneNumber } from '@dreryk/phone-number-common-api';
import { PatientId } from '@dreryk/egabinet-patient-published-language-api';

import {
   assertUnreachable,
   createTranslatedText,
   patientNameFormatter,
   phoneNumberFormatter,
   TranslatableText,
} from '@dreryk/react-common';
import { Link, SecondaryText, useHistoryPushWithBackAction } from '@dreryk/react-components';

import { patientPaths } from '~libs.desktop.shared.paths';

export type Patient = {
   id: number | PatientId;
   firstName?: FirstName;
   lastName?: LastName;
   phoneNumber?: PhoneNumber;
};

export type ExternalPatient =
   | Patient
   | {
        patientId: number | PatientId;
        patientFirstName?: FirstName;
        patientLastName?: LastName;
        patientPhoneNumber?: PhoneNumber;
     };

const isPatientNamePresent = (externalPatient: ExternalPatient): boolean => {
   return (
      'firstName' in externalPatient ||
      'lastName' in externalPatient ||
      'patientFirstName' in externalPatient ||
      'patientLastName' in externalPatient
   );
};

const getPatientPhoneNumber = (externalPatient: ExternalPatient): PhoneNumber | undefined => {
   if ('patientPhoneNumber' in externalPatient) {
      return externalPatient.patientPhoneNumber;
   }
   if ('phoneNumber' in externalPatient) {
      return externalPatient.phoneNumber;
   }
   return undefined;
};

const getPatientId = (externalPatient: ExternalPatient): number => {
   if ('patientId' in externalPatient) {
      return typeof externalPatient.patientId === 'number'
         ? externalPatient.patientId
         : externalPatient.patientId.asNumber();
   }
   if ('id' in externalPatient) {
      return typeof externalPatient.id === 'number' ? externalPatient.id : externalPatient.id.asNumber();
   }
   return assertUnreachable(undefined);
};

type PatientTableCellPresenterProps = {
   patient: ExternalPatient;
   goBackLinkLabel?: TranslatableText;
};

export const PatientTableCellPresenter = (props: PatientTableCellPresenterProps): JSX.Element => {
   const formattedPatientPhone = phoneNumberFormatter(getPatientPhoneNumber(props.patient));
   const push = useHistoryPushWithBackAction(true);
   const handleClick = useCallback(() => {
      push(patientPaths.getDataPath(getPatientId(props.patient)), {
         label: props.goBackLinkLabel,
      });
   }, [push, props.patient, props.goBackLinkLabel]);

   return (
      <div>
         {isPatientNamePresent(props.patient) ? (
            <Link
               testId={'patientName'}
               label={createTranslatedText(patientNameFormatter(props.patient) ?? '')}
               onClick={handleClick}
            />
         ) : null}
         {formattedPatientPhone ? (
            <SecondaryText text={createTranslatedText(formattedPatientPhone)} testId={'patientPhoneNumber'} />
         ) : null}
      </div>
   );
};
